import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import MongoDBSourceForm from './forms/MongoDBSourceForm';
import PostgresSourceForm from './forms/PostgresSourceForm';
import MinIOSourceForm from './forms/MinIOSourceForm';
import FirebaseSourceForm from './forms/FirebaseSourceForm';
import PythonSampleBlock from './PythonSampleBlock';
import { SOURCE_TYPES, DEFAULT_CONFIG, MASKED_PLACEHOLDER, PYTHON_SAMPLES, getFirebaseSampleCode } from '../constants';

const FORMS = {
    mongodb: MongoDBSourceForm,
    postgres: PostgresSourceForm,
    minio: MinIOSourceForm,
    firebase: FirebaseSourceForm
};

function buildConfigForSubmit(sourceType, config, isEdit) {
    const c = { ...config };
    const maskKeys = ['password', 'passwd', 'secret_key', 'secretKey', 'credentials_json'];
    const maskedValues = [MASKED_PLACEHOLDER, '***'];
    maskKeys.forEach((key) => {
        if (maskedValues.includes(c[key]) || (isEdit && (c[key] === '' || c[key] == null))) {
            delete c[key];
        }
    });
    if (sourceType === 'mongodb' && c.uri) {
        delete c.host;
        delete c.port;
        delete c.database;
        delete c.username;
        delete c.password;
    }
    if (sourceType === 'firebase' && typeof c.credentials_json === 'string') {
        try {
            c.credentials_json = c.credentials_json.trim() ? JSON.parse(c.credentials_json) : null;
        } catch {
            delete c.credentials_json;
        }
    }
    return c;
}

function validate(sourceType, name, config) {
    const err = {};
    if (!name || !String(name).trim()) err.name = 'Bắt buộc';
    if (sourceType === 'mongodb') {
        const hasUri = config?.uri?.trim();
        if (!hasUri && (!config?.host || !config?.database)) err.host = 'Cần URI hoặc Host + Database';
    }
    if (sourceType === 'postgres') {
        if (!config?.host?.trim()) err.host = 'Bắt buộc';
        if (!config?.database?.trim()) err.database = 'Bắt buộc';
    }
    if (sourceType === 'minio') {
        if (!config?.endpoint?.trim()) err.endpoint = 'Bắt buộc';
    }
    if (sourceType === 'firebase') {
        // Firestore không cần databaseURL; Realtime Database thì cần. Cho phép để trống khi dùng Firestore.
    }
    return err;
}

const SourceFormDialog = ({ open, onClose, sourceTypeId, editingSource, onSave, isSaving = false }) => {
    const typeConfig = SOURCE_TYPES.find((t) => t.id === sourceTypeId) || SOURCE_TYPES[0];
    const isEdit = !!editingSource;
    const defaultConfig = DEFAULT_CONFIG[sourceTypeId] || {};

    const [name, setName] = useState('');
    const [config, setConfig] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            if (editingSource) {
                setName(editingSource.name || '');
                setConfig(typeof editingSource.config === 'object' ? { ...editingSource.config } : {});
            } else {
                setName('');
                setConfig({ ...defaultConfig });
            }
            setErrors({});
        }
    }, [open, editingSource, sourceTypeId, defaultConfig]);

    const handleFormChange = ({ name: n, config: c }) => {
        setName(n);
        setConfig(c);
        setErrors({});
    };

    const handleSave = () => {
        const err = validate(sourceTypeId, name, config);
        if (Object.keys(err).length > 0) {
            setErrors(err);
            return;
        }
        const configToSend = buildConfigForSubmit(sourceTypeId, config, isEdit);
        onSave({ name: name.trim(), config: configToSend });
        onClose();
    };

    const FormComponent = FORMS[sourceTypeId];
    if (!FormComponent) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Sửa nguồn' : 'Thêm nguồn'} – {typeConfig.label}</DialogTitle>
            <DialogContent>
                <FormComponent
                    name={name}
                    config={config}
                    errors={errors}
                    onChange={handleFormChange}
                    isEdit={isEdit}
                />
                {(sourceTypeId === 'firebase' ? getFirebaseSampleCode(config) : PYTHON_SAMPLES[sourceTypeId]) && (
                    <PythonSampleBlock
                        sampleCode={sourceTypeId === 'firebase' ? getFirebaseSampleCode(config) : PYTHON_SAMPLES[sourceTypeId]}
                        label="Mã mẫu Python – kết nối tới nguồn này"
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Hủy</Button>
                <Button variant="contained" onClick={handleSave} disabled={isSaving} startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}>
                    {isSaving ? 'Đang lưu...' : (isEdit ? 'Cập nhật' : 'Thêm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SourceFormDialog;
